/* eslint-disable react/no-danger */
import React, { useState, useEffect, useMemo, useRef } from "react";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { useTranslation } from "react-i18next";
import { CircularProgress, ScrollShadow } from "@nextui-org/react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { BsHeadset, BsPlus } from "react-icons/bs";
import { MdCheckCircle } from "react-icons/md";
import { FaChevronLeft, FaChevronRight, FaRegUser } from "react-icons/fa";
import { BiSearchAlt } from "react-icons/bi";

import LeftPanel from "./LeftPanel";
import HeaderBar from "./HeaderBar";
import ActionsMenu from "./ActionsMenu";
import ListsMenu from "./ListsMenu";
import SummaryBlock from "./SummaryBlock";
import ChaptersBlock from "./ChaptersBlock";
import ExtractedFactsGrid from "./ExtractedFactsGrid";
import CrmMemoBlock from "./CrmMemoBlock";
import RightPanel from "./RightPanel";
import usePopupAudioSeek from "./usePopupAudioSeek";
import { formatCallDuration, srtTimeToSeconds } from "./utils/timecode";
import {
  buildDebriefOperators,
  isCallInDebrief,
  selectAddableDebriefs,
  toDebriefTs,
} from "./utils/debriefPicker";

import { MagicWandIcon } from "../../../assets/icons/audio-icons";
import myAxios from "../../../utils/axiosWrapper";
import { downloadCallAudioBlob } from "../../../utils/downloadCallAudio";
import { fetchLists } from "../../../redux/actions/listsActions";
import { auth } from "../../../conf/firebase";
import getConfig from "../../../conf/config";
import { addCallsToDebrief } from "../../../redux/actions/debriefActions";
import { useAppContext } from "../../../contexts/AppContext";
import Modal from "../../../components/Modal";
import CreateDebriefModal from "../../debriefs/CreateDebriefModal";

// Call audio is kept 90 days: past that, a download failure is expected.
const AUDIO_RETENTION_DAYS = 90;

// `call_timestamp` is epoch seconds on most calls, an ISO string on some.
const getCallDate = (call_timestamp) => {
  if (call_timestamp === null || call_timestamp === undefined || call_timestamp === "") return null;
  const date =
    typeof call_timestamp === "number" ? new Date(call_timestamp * 1000) : new Date(call_timestamp);
  return Number.isNaN(date.getTime()) ? null : date;
};

const CallModal = ({
  item: itemFromProps,
  handleOnClose = () => {},
  onOpenFullTranscript,
  onSearchNumber = () => {},
  display_tz = "Europe/Paris",
}) => {
  const [item, setItem] = useState(itemFromProps || {});
  let { id: itemId } = item;
  let displayableId = itemId ? `...${itemId.substring(itemId.length - 6)}` : "";

  const { t } = useTranslation();
  const seekPopupAudio = usePopupAudioSeek();
  const { popupAudioDuration, popupCallId } = useAppContext();
  const [isAudioDownloading, setIsAudioDownloading] = useState(false);
  const [leftMenuTab, setLeftMenuTab] = useState("summary"); // summary | transcription

  // Live content search state
  const transcriptWrapperRef = useRef(null);
  const [searchInput, setSearchInput] = useState("");
  const [focusedIndex, setFocusedIndex] = useState(0);

  const [comments, setComments] = useState([]);
  const [isCommentsLoading, setIsCommentsLoading] = useState(false);
  const [commentInputText, setCommentInputText] = useState("");
  const [isCommentPosting, setIsCommentPosting] = useState(false);
  const [isDebriefMenuDisplayed, setIsDebriefMenuDisplayed] = useState(false);
  const [debriefsList, setDebriefsList] = useState([]);
  const [debriefOperatorMap, setDebriefOperatorMap] = useState({});
  const [debriefSearch, setDebriefSearch] = useState("");
  const [debriefLoading, setDebriefLoading] = useState(false);
  const [debriefFetchError, setDebriefFetchError] = useState(false);
  const [showAllDebriefOperators, setShowAllDebriefOperators] = useState(false);
  const [addingToDebriefId, setAddingToDebriefId] = useState(null);
  const [debriefAddErrors, setDebriefAddErrors] = useState({});
  const [isCreateDebriefOpen, setIsCreateDebriefOpen] = useState(false);
  const { currentUser } = auth;

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { lists, loading } = useSelector((state) => state.lists.lists);
  const activeLists = lists.filter((list) => !list.is_archived && !list.is_deleted);
  const {
    scopes,
    company_config,
    loading: algoliaLoading,
  } = useSelector((state) => state.algolia.algolia);
  const { user } = useSelector((state) => state.user);

  const userPermissions = user?.permissions || [];
  const hasCommentsFeature =
    userPermissions.includes("SUPER_ADMIN") || userPermissions.includes("COMMENTS");
  const isSuperAdmin = userPermissions.includes("SUPER_ADMIN");
  const isEasierAdmin = user?.is_easier_admin;
  const canSeeLists = isSuperAdmin;
  const canSeeDebriefs = isSuperAdmin;

  const teamsScope = scopes?.teams || [];
  const departmentsScope = scopes?.departments || [];
  const localisationsScope = scopes?.localisations || [];
  const hasGlobalVisibility =
    teamsScope.includes("all") ||
    departmentsScope.includes("all") ||
    localisationsScope.includes("all");

  const dispatchFetchLists = () => {
    dispatch(fetchLists());
  };

  useEffect(() => {
    if (!currentUser || currentUser === null) {
      return;
    }

    dispatchFetchLists();
  }, [user]);

  const isPastAudioRetention = useMemo(() => {
    const callDate = getCallDate(item?.call_timestamp);
    if (!callDate) return false;

    const diffInDays = Math.floor((Date.now() - callDate.getTime()) / (1000 * 3600 * 24));
    return diffInDays > AUDIO_RETENTION_DAYS;
  }, [item?.call_timestamp]);

  // Older Algolia hits may lack `call_duration_seconds`: fall back to the length
  // the modal's player read from the audio file, when it is this call's.
  const callDurationLabel =
    formatCallDuration(item?.call_duration_seconds) ||
    (popupCallId === itemId ? formatCallDuration(popupAudioDuration) : null);

  const fetchComments = async (callId) => {
    if (!hasCommentsFeature) {
      return;
    }

    setIsCommentsLoading(true);
    const response = await myAxios.get(`${getConfig().API_URL}/audio/api/v1/comments/${callId}`);
    const retrievedComments = response?.data?.[0]?.comments || [];

    setComments(retrievedComments);
    setIsCommentsLoading(false);
  };

  useEffect(() => {
    if (!itemId) {
      return;
    }

    if (!currentUser || currentUser === null) {
      return;
    }

    fetchComments(itemId);
  }, [itemId, user]);

  const onPostComment = async (callId, content, audioTimestamp = null) => {
    if (!content || content.trim().length <= 0 || isCommentPosting) {
      return;
    }

    const payload = { content };
    if (Number.isFinite(audioTimestamp) && audioTimestamp > 0) {
      payload.audio_timestamp = audioTimestamp;
    }

    setIsCommentPosting(true);
    try {
      await myAxios.post(`${getConfig().API_URL}/audio/api/v1/comments/${callId}`, payload);
      setCommentInputText("");
      fetchComments(callId);
    } catch (error) {
      console.error("Error posting comment:", error);
      toast.error(t("Le message n'a pas pu être envoyé"));
    }
    setIsCommentPosting(false);
  };

  const onSaveOverview = async (nextOverview) => {
    const response = await myAxios.put(
      `${getConfig().API_URL}/audio/api/v1/calls/${itemId}/overview`,
      { overview: nextOverview }
    );
    const saved = response?.data?.[0] || {};

    setItem((previousItem) => ({
      ...previousItem,
      analysis: {
        ...previousItem.analysis,
        overview: saved.overview ?? nextOverview,
        overview_edited_at: saved.overview_edited_at,
        overview_edited_by: saved.overview_edited_by,
        overview_edited_by_id: saved.overview_edited_by_id,
      },
    }));

    // The edit is logged as an activity entry, so the feed has to catch up.
    fetchComments(itemId);
  };

  const setIsNotApplicable = async (item) => {
    await myAxios.put(
      `${getConfig().API_URL}/audio/api/v1/calls/set_call_not_applicable/${item.id}`
    );

    // update local state
    const newItem = {
      ...item,
      analysis: {
        ...item.analysis,
        call_overall_grade: -1,
        status_label: "marked_not_applicable",
      },
    };
    setItem(newItem);
  };

  const formatAudioFileName = (item, fileExtension = "wav") => {
    const dateBloc = (getCallDate(item?.call_timestamp) || new Date())
      .toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
      .replace(/,/g, "")
      .replace(/:/g, "_");

    return `${(item?.operator_name || "appel").toUpperCase()}_${dateBloc}.${fileExtension}`;
  };

  const callOperatorId = item?.operator_id;

  const fetchDebriefsList = async () => {
    setDebriefLoading(true);
    setDebriefFetchError(false);
    try {
      const [debriefRes, operatorRes] = await Promise.all([
        myAxios.get(`${getConfig().API_URL}/audio/api/v1/debriefs`),
        myAxios.get(`${getConfig().API_URL}/audio/api/v1/admin/operators`),
      ]);
      const debriefs = debriefRes?.data?.[0]?.debriefs || [];
      const operatorMap = operatorRes?.data?.[0]?.operators || {};

      const enriched = debriefs.map((d) => {
        const op = operatorMap[d.operator_id];
        return {
          ...d,
          operator_name: op ? `${op.first_name} ${op.last_name}` : d.operator_id,
        };
      });
      setDebriefsList(enriched);
      setDebriefOperatorMap(operatorMap);
    } catch (error) {
      console.error("Error fetching debriefs:", error);
      setDebriefFetchError(true);
    }
    setDebriefLoading(false);
  };

  const onOpenDebriefMenu = () => {
    setIsDebriefMenuDisplayed(!isDebriefMenuDisplayed);
    if (!isDebriefMenuDisplayed) {
      fetchDebriefsList();
      setDebriefSearch("");
      setShowAllDebriefOperators(!callOperatorId);
      setDebriefAddErrors({});
    }
  };

  const onAddCallToDebrief = async (debriefId) => {
    if (addingToDebriefId) return;

    setAddingToDebriefId(debriefId);
    setDebriefAddErrors((previous) => ({ ...previous, [debriefId]: null }));
    try {
      const added = await addCallsToDebrief(debriefId, [itemId]);
      if (added === false) throw new Error("Not authenticated");

      // Mark the call as part of the debrief so the row stays "already added".
      setDebriefsList((previous) =>
        previous.map((d) =>
          d.id === debriefId ? { ...d, call_ids: [...(d.call_ids || []), itemId] } : d
        )
      );
    } catch (error) {
      console.error("Error adding call to debrief:", error);
      setDebriefAddErrors((previous) => ({
        ...previous,
        [debriefId]: t("L'appel n'a pas pu être ajouté, réessayez"),
      }));
    }
    setAddingToDebriefId(null);
  };

  const debriefOperators = useMemo(
    () => buildDebriefOperators(debriefOperatorMap, callOperatorId),
    [debriefOperatorMap, callOperatorId]
  );

  const onOpenCreateDebrief = () => {
    setIsDebriefMenuDisplayed(false);
    setIsCreateDebriefOpen(true);
  };

  const onOpenDebrief = (debriefId) => {
    handleOnClose();
    navigate(`/debriefs/${debriefId}`);
  };

  const onCreateDebriefSuccess = async (newDebriefId) => {
    setIsCreateDebriefOpen(false);
    if (!newDebriefId) return;

    let isCallAdded = true;
    try {
      const added = await addCallsToDebrief(newDebriefId, [itemId]);
      isCallAdded = added !== false;
    } catch (error) {
      console.error("Error adding call to the new debrief:", error);
      isCallAdded = false;
    }

    const renderToast = ({ closeToast }) => (
      <div className="flex flex-col gap-1 text-sm">
        <span>
          {isCallAdded
            ? t("Débrief créé, l'appel y a été ajouté")
            : t("Débrief créé, mais l'appel n'a pas pu y être ajouté")}
        </span>
        <button
          type="button"
          className="self-start font-medium text-primary hover:underline"
          onClick={() => {
            closeToast();
            onOpenDebrief(newDebriefId);
          }}
        >
          {t("Ouvrir le débrief")}
        </button>
      </div>
    );

    if (isCallAdded) {
      toast.success(renderToast, { autoClose: 8000 });
    } else {
      toast.error(renderToast, { autoClose: 8000 });
    }
  };

  const filteredDebriefs = useMemo(
    () =>
      selectAddableDebriefs({
        debriefs: debriefsList,
        operator_id: callOperatorId,
        show_all_operators: showAllDebriefOperators,
        search: debriefSearch,
      }),
    [debriefsList, callOperatorId, showAllDebriefOperators, debriefSearch]
  );

  const formatDebriefDate = (date) => {
    const ts = toDebriefTs(date);
    if (!ts) return null;
    return new Date(ts * 1000).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatDebriefPeriod = (debrief) => {
    const start = formatDebriefDate(debrief.period_start);
    const end = formatDebriefDate(debrief.period_end);
    if (start && end) return `${start} → ${end}`;
    return start || end || null;
  };

  const onShareAudio = (item) => {
    if (!item) {
      return;
    }

    const baseUrl = getConfig().BASE_URL;
    const callLink = `${baseUrl}/calls/${item?.id}`;

    const mailtoLink = `mailto:?subject=Partage de Call Audio&body=${encodeURIComponent(
      `Voici le lien vers le call :
${callLink}`
    )}`;
    window.open(mailtoLink, "_blank");
  };

  const exportAudioXlsx = async (item) => {
    if (!item) {
      return;
    }

    const fileName = formatAudioFileName(item, "xlsx");
    const baseUrl = getConfig().BASE_URL;
    const audioUrl = item?.audio_url;
    const callLink = `${baseUrl}/calls/${item?.id}`;

    const rows = [
      {
        id: item.id,
        audio_dl: audioUrl,
        call_link: callLink,
        operator_name: item?.operator_name || "",
        client_number: item?.client_number || "",
        call_summary: item?.analysis?.call_summary || "",
        from: item.incoming ? "incoming" : "outgoing",
        grade: item?.analysis?.call_overall_grade?.toFixed(1) || -1,
        dynamism: item?.analysis?.dynamism || -1,
        call_structure_respect: item?.analysis?.call_structure_respect || -1,
        client_sentiment: item?.analysis?.client_sentiment || "",
        agent_tone: item?.analysis?.agent_tone || "",
        team: item?.team || "",
        department: item?.department || "",
      },
    ];
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Appel");
    XLSX.writeFile(workbook, fileName, { compression: false });
  };

  // Goes through the backend download endpoint: the stored audio URL is not
  // fetchable from the browser and may have expired.
  const onDownloadAudio = async () => {
    if (!item?.id || isAudioDownloading) return;

    if (!item?.audio_url) {
      toast.error(t("Aucun audio disponible pour cet appel"));
      return;
    }

    setIsAudioDownloading(true);
    const toastId = toast.loading(t("Téléchargement de l'audio..."));
    try {
      const audioBlob = await downloadCallAudioBlob(item.id);
      saveAs(audioBlob, formatAudioFileName(item));
      toast.dismiss(toastId);
    } catch (error) {
      console.error("Error downloading call audio:", error);
      toast.update(toastId, {
        render: isPastAudioRetention
          ? t("L'audio de cet appel n'est plus disponible (plus de 90 jours)")
          : t("Le téléchargement de l'audio a échoué"),
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
    }
    setIsAudioDownloading(false);
  };

  const isIncomingCall = item?.incoming;
  const isOutgoingCall = !isIncomingCall;

  const salePurposes = company_config?.sale_purposes_for_notation || [];
  const to_retrieve_fields = company_config?.to_retrieve || [];
  const callPurpose = item?.analysis?.call_purpose;
  const isSalePurposeCall = salePurposes.includes(callPurpose);
  const isSiderCustomGrading = company_config?.custom_grading === "sider";

  const hasDpt = item?.department?.length > 0;
  const overallGrade = item?.analysis?.call_overall_grade;
  // Already-ignored calls have nothing left to mark.
  const canMarkNotApplicable =
    !!item?.analysis && overallGrade !== -1 && overallGrade !== null && overallGrade !== undefined;

  const renderDebriefRow = (debrief) => {
    const isAlreadyAdded = isCallInDebrief(debrief, itemId);
    const isAdding = addingToDebriefId === debrief.id;
    const addError = debriefAddErrors[debrief.id];
    const period = formatDebriefPeriod(debrief);
    const scheduledDate = formatDebriefDate(debrief.scheduled_date);

    return (
      <div key={debrief.id}>
        <button
          type="button"
          className={`w-full text-left px-3 py-2.5 rounded-lg transition-colors flex items-center gap-3 ${
            isAlreadyAdded ? "cursor-default" : "hover:bg-gray-50"
          }`}
          onClick={() => onAddCallToDebrief(debrief.id)}
          disabled={isAlreadyAdded || !!addingToDebriefId}
        >
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate capitalize">
              {debrief.operator_name || debrief.operator_id}
            </p>
            <p className="text-xs text-gray-500 truncate">
              {period ? `${t("Période")} : ${period}` : t("Période non définie")}
            </p>
            <p className="text-xs text-gray-400">
              {scheduledDate && <span>{`${t("Planifié le")} ${scheduledDate}`}</span>}
              <span
                className={`${scheduledDate ? "ml-2 " : ""}${
                  debrief.status === "scheduled" ? "text-yellow-500" : "text-gray-400"
                }`}
              >
                {debrief.status === "scheduled" ? t("Planifié") : t("Brouillon")}
              </span>
            </p>
          </div>
          {isAdding && (
            <CircularProgress
              size="sm"
              color="primary"
              aria-label="Loading..."
              classNames={{ svg: "w-[18px] h-[18px]" }}
            />
          )}
          {!isAdding && isAlreadyAdded && (
            <span className="flex items-center gap-1 text-xs text-teal-600 shrink-0">
              <MdCheckCircle
                size={16}
                className="text-teal-500"
              />
              {t("Déjà ajouté")}
            </span>
          )}
        </button>
        {addError && <p className="px-3 pb-1 text-xs text-danger">{addError}</p>}
      </div>
    );
  };

  const renderDebriefFloatingMenu = () => {
    const isFilteredOnOperator = !!callOperatorId && !showAllDebriefOperators;
    const operatorTabClass = (isActive) =>
      `flex-1 truncate rounded-md px-2 py-1 text-xs font-medium transition-colors ${
        isActive ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
      }`;

    return (
      <div className="mt-1.5 bg-white rounded-lg p-3 text-[#33403F] drop-shadow-xl w-[340px] max-h-[min(460px,60vh)] flex flex-col">
        <p className="text-sm font-semibold text-gray-900 mb-2">{t("Ajouter à un débrief")}</p>
        {callOperatorId && (
          <div className="flex gap-1 rounded-lg bg-gray-100 p-1 mb-2">
            <button
              type="button"
              className={`${operatorTabClass(isFilteredOnOperator)} capitalize`}
              onClick={() => setShowAllDebriefOperators(false)}
            >
              {item?.operator_name || t("Cet opérateur")}
            </button>
            <button
              type="button"
              className={operatorTabClass(!isFilteredOnOperator)}
              onClick={() => setShowAllDebriefOperators(true)}
            >
              {t("Tous les opérateurs")}
            </button>
          </div>
        )}
        {!isFilteredOnOperator && (
          <div className="flex items-center rounded-lg border border-gray-200 bg-white px-2 mb-2">
            <BiSearchAlt
              size={16}
              className="text-gray-400 shrink-0"
            />
            <input
              className="outline-none border-0 flex-1 px-2 py-2 text-sm"
              placeholder={t("Rechercher par représentant...")}
              value={debriefSearch}
              onChange={(e) => setDebriefSearch(e.target.value)}
            />
          </div>
        )}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {debriefLoading && (
            <div className="flex items-center justify-center py-4">
              <CircularProgress
                size="sm"
                color="primary"
                aria-label="Loading..."
              />
            </div>
          )}
          {!debriefLoading && debriefFetchError && (
            <div className="flex flex-col items-center gap-2 py-4 text-sm text-danger">
              {t("Impossible de charger les débriefs")}
              <button
                type="button"
                className="text-xs font-medium text-primary hover:underline"
                onClick={fetchDebriefsList}
              >
                {t("Réessayer")}
              </button>
            </div>
          )}
          {!debriefLoading && !debriefFetchError && filteredDebriefs.length === 0 && (
            <div className="text-center text-gray-400 text-sm py-4">
              {isFilteredOnOperator
                ? t("Aucun débrief en cours pour cet opérateur")
                : t("Aucun debrief trouvé")}
            </div>
          )}
          {!debriefLoading && !debriefFetchError && filteredDebriefs.map(renderDebriefRow)}
        </div>
        <div className="border-t border-gray-100 mt-2 pt-2">
          <button
            type="button"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-primary hover:bg-primary/10 transition-colors disabled:opacity-50"
            onClick={onOpenCreateDebrief}
            disabled={debriefLoading || debriefFetchError}
          >
            <BsPlus size={18} />
            {callOperatorId ? t("Nouveau débrief pour cet opérateur") : t("Nouveau débrief")}
          </button>
        </div>
      </div>
    );
  };

  // Live content search - count hits
  const nbHits = useMemo(() => {
    if (!searchInput || searchInput.length === 0) return 0;
    const transcript = item?.transcript || "";
    if (typeof transcript !== "string") return 0;
    return transcript.toLowerCase().split(searchInput.toLowerCase()).length - 1;
  }, [searchInput, item?.transcript]);

  // Navigate through search results
  const onUpdateFocusedIndex = (diff) => () => {
    if (searchInput?.length <= 0 || nbHits === 0) return;

    const newHitIdx = (focusedIndex + diff + nbHits) % nbHits;
    setFocusedIndex(newHitIdx);

    if (!transcriptWrapperRef.current) return;

    const elements = transcriptWrapperRef.current.querySelectorAll("em");
    if (elements?.length <= newHitIdx) return;

    const targetElement = elements[newHitIdx];
    const targetElementOffsetTop = targetElement.offsetTop;
    transcriptWrapperRef.current.scrollTo({
      top: targetElementOffsetTop - 100,
      behavior: "smooth",
    });
  };

  // Render transcript with search highlighting
  const renderTranscriptWithSearch = (transcript) => {
    const isIncoming = item?.incoming;
    const splitTranscript = transcript.split("\n\n");

    return (
      <div
        className="relative"
        ref={transcriptWrapperRef}
      >
        <div className="whitespace-pre-wrap">
          {splitTranscript.map((block, blockIndex) => {
            const splitBlock = block.split("\n");

            if (splitBlock.length < 3) return null;

            const timeMatch = splitBlock[1].match(
              /^(\d+)\s*(\d+:\d+:\d+,\d+)\s*-->\s*(\d+:\d+:\d+,\d+)\s*(.*)$/
            );

            if (!timeMatch) return null;

            const startTime = splitBlock[1].substring(3, 8);
            const startSeconds = srtTimeToSeconds(splitBlock[1].split("-->")[0]);
            const text = splitBlock[2];
            const splitText = text.split(": ");
            let displayText = text;
            let actorPicto = null;

            if (splitText.length === 2) {
              displayText = splitText[1];
              if (isIncoming) {
                actorPicto =
                  splitText[0] === "0" ? <BsHeadset size={20} /> : <FaRegUser size={20} />;
              } else {
                actorPicto =
                  splitText[0] === "0" ? <FaRegUser size={20} /> : <BsHeadset size={20} />;
              }
            }

            if (searchInput && searchInput.length > 0) {
              const regex = new RegExp(`(${searchInput})`, "gi");
              displayText = displayText.replace(
                regex,
                "<em class='bg-yellow-200 px-0.5 rounded'>$1</em>"
              );
            }

            return (
              <div
                key={`b-${blockIndex}`}
                className="mb-3"
              >
                <div className="flex items-start gap-2">
                  {actorPicto && (
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                      {actorPicto}
                    </div>
                  )}
                  <div className="flex-1">
                    {startSeconds !== null ? (
                      <button
                        type="button"
                        onClick={() => seekPopupAudio(startSeconds)}
                        title={`${t("Listen from")} ${startTime}`}
                        className="bg-primary text-white rounded-md px-2 py-0.5 inline-block mb-1 hover:opacity-80 transition-opacity"
                      >
                        <span className="text-xs">{startTime}</span>
                      </button>
                    ) : (
                      <div className="bg-primary text-white rounded-md px-2 py-0.5 inline-block mb-1">
                        <span className="text-xs">{startTime}</span>
                      </div>
                    )}
                    <div
                      className="text-sm text-gray-700"
                      dangerouslySetInnerHTML={{ __html: displayText }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Simple transcript display (no search)
  const renderTranscript = () => {
    const transcript = item?.transcript;

    if (!transcript || (typeof transcript === "string" && transcript.length === 0)) {
      return (
        <div className="flex items-center justify-center h-full text-gray-400">
          <p>{t("Aucune transcription disponible")}</p>
        </div>
      );
    }

    if (typeof transcript === "string") {
      return renderTranscriptWithSearch(transcript);
    }

    return (
      <div className="text-sm text-gray-700 whitespace-pre-wrap">
        {JSON.stringify(transcript, null, 2)}
      </div>
    );
  };

  return (
    <div className="flex w-full h-full overflow-hidden bg-white">
      <LeftPanel
        item={item}
        left_menu_tab={leftMenuTab}
        set_left_menu_tab={setLeftMenuTab}
        has_dpt={hasDpt}
        display_tz={display_tz}
      />
      <div className="flex-1 flex flex-col min-w-0 bg-white">
        <HeaderBar
          title={leftMenuTab === "summary" ? t("Détails de l'appel") : t("Transcription")}
          item_id={itemId}
          displayable_id={displayableId}
          call_purpose={callPurpose}
          is_incoming_call={isIncomingCall}
          overall_grade={item?.analysis?.call_overall_grade}
          duration_label={callDurationLabel}
          actions_menu={
            <>
              {canSeeLists && (
                <ListsMenu
                  call_id={itemId}
                  lists={activeLists}
                  on_lists_changed={dispatchFetchLists}
                />
              )}
              <ActionsMenu
                can_see_debriefs={canSeeDebriefs}
                on_share_audio={() => onShareAudio(item)}
                on_download_audio={onDownloadAudio}
                on_open_debrief_menu={onOpenDebriefMenu}
                is_debrief_menu_displayed={isDebriefMenuDisplayed}
                set_is_debrief_menu_displayed={setIsDebriefMenuDisplayed}
                render_debrief_floating_menu={renderDebriefFloatingMenu}
                can_mark_not_applicable={canMarkNotApplicable}
                on_mark_not_applicable={() => setIsNotApplicable(item)}
              />
            </>
          }
        />
        <ScrollShadow
          hideScrollBar
          className="flex-1 px-8 pb-8 overflow-y-auto"
        >
          {leftMenuTab === "summary" && (
            <div className="flex flex-col gap-4">
              <SummaryBlock
                overview={item?.analysis?.overview}
                overview_timecodes={item?.analysis?.overview_timecodes}
                on_seek={seekPopupAudio}
                fallback_text={item?.analysis?.call_summary}
                edited_by={item?.analysis?.overview_edited_by}
                edited_at={item?.analysis?.overview_edited_at}
                can_edit={isSuperAdmin}
                on_save={onSaveOverview}
              />
              {isSiderCustomGrading && <CrmMemoBlock memo={item?.analysis?.crm_memo} />}
              <ChaptersBlock
                chapters={item?.analysis?.chapters}
                on_seek={seekPopupAudio}
              />
              <ExtractedFactsGrid
                analysis={item?.analysis}
                company_config={company_config}
                has_global_visibility={hasGlobalVisibility}
                is_sale_purpose_call={isSalePurposeCall}
                is_outgoing_call={isOutgoingCall}
              />
            </div>
          )}
          {leftMenuTab === "transcription" && (
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center rounded-lg border border-gray-200 bg-white pl-3 text-sm flex-1 mr-4">
                  <MagicWandIcon />
                  <input
                    className="outline-none border-0 flex-1 px-2 py-2"
                    placeholder={t("Rechercher dans la transcription...")}
                    value={searchInput}
                    onChange={(e) => {
                      setSearchInput(e.target.value);
                      setFocusedIndex(0);
                    }}
                  />
                  <button
                    type="button"
                    onClick={onUpdateFocusedIndex(-1)}
                    className="cursor-pointer hover:bg-gray-100 border-l border-gray-200 p-2"
                  >
                    <FaChevronLeft
                      color="#737373"
                      size={12}
                    />
                  </button>
                  <button
                    type="button"
                    onClick={onUpdateFocusedIndex(1)}
                    className="cursor-pointer hover:bg-gray-100 border-l border-gray-200 p-2"
                  >
                    <FaChevronRight
                      color="#737373"
                      size={12}
                    />
                  </button>
                </div>
                <div className="text-sm text-gray-500 whitespace-nowrap">
                  {nbHits > 0 ? `${Math.min(nbHits, focusedIndex + 1)}/${nbHits}` : "0"}{" "}
                  {t("résultats")}
                </div>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 flex-1 overflow-y-auto">
                {renderTranscript()}
              </div>
            </div>
          )}
        </ScrollShadow>
      </div>
      <RightPanel
        analysis={item?.analysis}
        company_config={company_config}
        is_sider={isSiderCustomGrading}
        has_comments_feature={hasCommentsFeature}
        comments_props={{
          comments,
          is_loading: isCommentsLoading,
          is_posting: isCommentPosting,
          comment_input_text: commentInputText,
          set_comment_input_text: setCommentInputText,
          on_post_comment: onPostComment,
          on_seek: seekPopupAudio,
          fetch_comments: fetchComments,
          item_id: itemId,
        }}
        on_close={handleOnClose}
      />
      {isCreateDebriefOpen && (
        <Modal
          isOpen={isCreateDebriefOpen}
          showFooter={false}
          size="2xl"
          handleOnClose={() => setIsCreateDebriefOpen(false)}
        >
          <CreateDebriefModal
            operators={debriefOperators}
            defaultOperatorId={callOperatorId || ""}
            onClose={() => setIsCreateDebriefOpen(false)}
            onSuccess={onCreateDebriefSuccess}
          />
        </Modal>
      )}
    </div>
  );
};

export default CallModal;
